import React, { useCallback, useMemo } from 'react';
import { FiLoader } from '@vyaguta/vyaguta-icons/fi';
import { FloatingButtons } from '@vyaguta/vyaguta-ui';

import config from 'config';
import history from 'utils/history';
import { success } from 'utils/toast';
import { interpolate } from 'utils/string';
import { isObjectEmpty } from 'utils/object';

import FLOATING_BUTTONS, {
  BTN_STATES,
  MAX_LENGTH,
  BUTTON_LABEL,
  WORKLOG_BUTTON,
  ATTENDANCE_TYPE,
  MISSING_ATTENDANCE_BUTTON,
} from 'constants/floatingButtons';
import { SELF } from 'constants/fetchTypes';
import { mixPanelEvents } from 'constants/mixPanel';
import { ATTENDANCE_URL } from 'constants/endpoints';
import { H_MM, WEEKENDS_DAY_CODE } from 'constants/date';
import { WORKLOG_STATUS, WORK_LOCATION } from 'constants/worklog';
import { UPDATE_ATTENDANCE_STATUS_TOAST_MESSAGE } from 'constants/messages';

import { stringify } from 'utils/queryParam';
import { getCurrentDay, getFormattedDate, getTimeAtTimeZone, isTimePast } from 'utils/date';

import * as mixpanel from 'services/mixPanel';

import { useCountryCheck } from 'hooks/useCountryCheck';
import { useCurrentUser } from 'hooks/query/useUserInfo';
import { useSelfAttendance } from 'contexts/SelfAttendanceContext';
import { useFetchAttendanceCalendarQuery, useSaveWorklogMutation } from 'hooks/query/useAttendanceQueries';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

type WorkLocation = (typeof WORK_LOCATION)[keyof typeof WORK_LOCATION];
type AttendanceTypeId = (typeof ATTENDANCE_TYPE)[keyof typeof ATTENDANCE_TYPE];
type AttendanceButtonId = AttendanceTypeId;

type ButtonType = (typeof BTN_STATES)[keyof typeof BTN_STATES];

type WorklogStatus = (typeof WORKLOG_STATUS)[keyof typeof WORKLOG_STATUS];

type AttendanceWorklog = {
  id: string | number;
  location: WorkLocation;
  status: WorklogStatus;
  [key: string]: JsonValue;
};

type TodaySelfAttendance = {
  date: string;
  worklog: AttendanceWorklog | null;
  leave: { [key: string]: JsonValue } | null;
  holiday: { [key: string]: JsonValue } | null;
  isWeekend: number;
  [key: string]: JsonValue;
};

type LoggedInUser = {
  id: number | string;
  timezone: string;
  [key: string]: JsonValue;
};

type FloatingActionButton = {
  id: AttendanceButtonId;
  icon: React.ElementType;
  label: string;
  type?: ButtonType;
  onClick: (() => void) | null;
  isDisabled: boolean;
  allowTooltip: boolean;
};

type FloatingActionButtonConfig = Pick<FloatingActionButton, 'id' | 'icon' | 'label'>;

type AppConfig = {
  vyagutaURI: string;
  vyaguta: {
    attendance: string;
  };
  freezeAttendance: {
    timeForLock: string;
  };
  endpoints: {
    attendance: {
      app: {
        applyLeave: string;
        worklogDetail: string;
      };
    };
  };
};

const WorkPlanButtons: React.FC = () => {
  const hasRemoteApplicationAccess = useCountryCheck();

  const user = useCurrentUser() as LoggedInUser;

  const today = getFormattedDate();
  const { OFFICE, HOME } = WORK_LOCATION;

  const {
    selfAttendance: { missing: selfMissing, pending: selfPending },
    setSelfAttendance,
  } = useSelfAttendance();

  const { workPlanButton } = mixPanelEvents.select;

  const todaySelfAttendanceFilter = useMemo(
    () => ({
      startDate: today,
      endDate: today,
      fetchType: SELF,
    }),
    [today]
  );

  const { data: calendarData, isLoading } =
    useFetchAttendanceCalendarQuery<TodaySelfAttendance[]>(todaySelfAttendanceFilter);
  const { mutate: saveWorklog, isPending: isSavingWorklog } = useSaveWorklogMutation();

  const todaySelfAttendance = useMemo<TodaySelfAttendance>(() => {
    const emptyTodayData: TodaySelfAttendance = {
      date: getFormattedDate(),
      worklog: null,
      leave: null,
      holiday: null,
      isWeekend: 0,
    };

    const data =
      Array.isArray(calendarData) && calendarData.length > 0 ? (calendarData[0] as TodaySelfAttendance) : null;
    return data || emptyTodayData;
  }, [calendarData]);

  const handleMissingChange = useCallback(
    (prevData: number) => {
      const currentDay = getCurrentDay();

      if (WEEKENDS_DAY_CODE.includes(currentDay)) return prevData;
      if (todaySelfAttendance?.holiday) return prevData;
      if (!prevData) return prevData;

      return prevData - 1;
    },
    [todaySelfAttendance?.holiday]
  );

  const applyTodaysWorklog = useCallback(
    (location: WorkLocation) => {
      const worklog = {
        location,
        workDate: today,
      };

      saveWorklog(worklog, {
        onSuccess: () => {
          const toastMessageByLocation = UPDATE_ATTENDANCE_STATUS_TOAST_MESSAGE;
          success({ title: 'Success', message: toastMessageByLocation[location] });

          setSelfAttendance({ missing: handleMissingChange(Number(selfMissing) || 0), pending: selfPending + 1 });

          mixpanel.trackEvent(workPlanButton, { Category: location });
        },
      });
    },
    [today, saveWorklog, selfMissing, selfPending, workPlanButton, setSelfAttendance, handleMissingChange]
  );

  const applyWfh = useCallback(() => {
    applyTodaysWorklog(HOME);
  }, [HOME, applyTodaysWorklog]);

  const applyWfo = useCallback(() => {
    applyTodaysWorklog(OFFICE);
  }, [OFFICE, applyTodaysWorklog]);

  const applyLeave = useCallback(
    (queryParams?: Record<string, string | number | boolean | null | undefined>) => {
      const cfg = config as AppConfig;
      const url = `${cfg.vyagutaURI}${cfg.vyaguta.attendance}${cfg.endpoints.attendance.app.applyLeave}`;
      window.open(stringify(url, queryParams));
      mixpanel.trackEvent(workPlanButton, { Category: 'Apply Leave' });
    },
    [workPlanButton]
  );

  const isComponentLoading = isLoading || isSavingWorklog;

  const onRemoteApplicationClick = useCallback(() => {
    mixpanel.trackEvent(mixPanelEvents.remoteWorkApplication, { Source: 'FloatingActionButton', Date: today });
    window.open(stringify(ATTENDANCE_URL, { display: 'remoteWorkModal' }));
  }, [today]);

  const floatingButtons = useMemo<FloatingActionButton[]>(() => {
    if (!todaySelfAttendance || isObjectEmpty(todaySelfAttendance)) {
      return [];
    }

    const cfg = config as AppConfig;

    const isAttendanceLockedTime = isTimePast(cfg.freezeAttendance.timeForLock, getTimeAtTimeZone(user.timezone, H_MM));

    const isAttendanceLocked =
      !todaySelfAttendance.worklog &&
      !todaySelfAttendance.holiday &&
      !todaySelfAttendance.leave &&
      todaySelfAttendance.isWeekend === 0 &&
      isAttendanceLockedTime;

    const handleFloatingButtonsClick: Record<AttendanceTypeId, () => void> = {
      [ATTENDANCE_TYPE.LEAVE]: () =>
        applyLeave({ startDate: todaySelfAttendance.date, endDate: todaySelfAttendance.date }),
      [ATTENDANCE_TYPE.WFH]: () => void applyWfh(),
      [ATTENDANCE_TYPE.WFO]: () => void applyWfo(),
      [ATTENDANCE_TYPE.FREEZE]: () => {
        window.open(ATTENDANCE_URL);
      },
      [ATTENDANCE_TYPE.REMOTE_WORK]: () => onRemoteApplicationClick(),
    };

    if (isAttendanceLocked) {
      return [
        {
          ...(MISSING_ATTENDANCE_BUTTON as FloatingActionButton),
          isDisabled: false,
          allowTooltip: false,
          onClick: handleFloatingButtonsClick[ATTENDANCE_TYPE.FREEZE],
        },
      ];
    }

    const attendanceType: AttendanceTypeId | null = todaySelfAttendance.leave
      ? ATTENDANCE_TYPE.LEAVE
      : todaySelfAttendance.worklog
        ? todaySelfAttendance.worklog.location === OFFICE
          ? ATTENDANCE_TYPE.WFO
          : ATTENDANCE_TYPE.WFH
        : null;

    const baseButtons: FloatingActionButton[] = (FLOATING_BUTTONS as readonly FloatingActionButtonConfig[]).map(
      (btn) => ({
        ...btn,
        isDisabled: false,
        allowTooltip: false,
        onClick: handleFloatingButtonsClick[btn.id],
      })
    );

    const visibleBaseButtons = hasRemoteApplicationAccess
      ? baseButtons
      : baseButtons.filter((btn) => btn.id !== ATTENDANCE_TYPE.REMOTE_WORK);

    if (!attendanceType) {
      return visibleBaseButtons;
    }

    const newFloatingButtons: FloatingActionButton[] = visibleBaseButtons.map((btn) => {
      const isSelected = btn.id === attendanceType;
      const isDisabled = !isSelected;

      const isActionAllowedWhenDisabled = btn.id === ATTENDANCE_TYPE.LEAVE || btn.id === ATTENDANCE_TYPE.REMOTE_WORK;
      const effectiveDisabled = isDisabled && !isActionAllowedWhenDisabled;

      const nextType: ButtonType = isSelected ? BTN_STATES.SECONDARY : BTN_STATES.GRAYED;
      const labelMap = BUTTON_LABEL as Record<AttendanceButtonId, Record<string, string>>;
      const labelKey = effectiveDisabled ? BTN_STATES.DISABLED : nextType;
      const nextLabel = labelMap[btn.id][labelKey];

      return {
        ...btn,
        type: nextType,
        isDisabled: effectiveDisabled,
        allowTooltip: effectiveDisabled,
        onClick: effectiveDisabled ? null : btn.onClick,
        label: nextLabel,
      };
    });

    if (attendanceType !== ATTENDANCE_TYPE.LEAVE && todaySelfAttendance.worklog) {
      const isWorklogPending = todaySelfAttendance.worklog.status === WORKLOG_STATUS.PENDING;

      if (newFloatingButtons.length < MAX_LENGTH) {
        const worklogButton: FloatingActionButton = {
          ...(WORKLOG_BUTTON as FloatingActionButton),
          onClick: () => {
            const destinationPath = interpolate(
              `${cfg.vyagutaURI}${cfg.vyaguta.attendance}${cfg.endpoints.attendance.app.worklogDetail}`,
              { id: todaySelfAttendance.worklog?.id }
            );
            if (history.location.pathname === destinationPath) return;

            window.open(destinationPath);

            mixpanel.trackEvent(workPlanButton, { Category: "Fill today's worklog" });
          },
        };

        newFloatingButtons.unshift(worklogButton);
      }

      const worklogBtn = newFloatingButtons[0];
      const nextType: ButtonType = isWorklogPending ? BTN_STATES.PRIMARY : BTN_STATES.GRAYED;
      const effectiveDisabled = !isWorklogPending;
      const labelMap = BUTTON_LABEL as Record<AttendanceButtonId, Record<string, string>>;
      const labelKey = effectiveDisabled ? BTN_STATES.DISABLED : nextType;

      newFloatingButtons[0] = {
        ...worklogBtn,
        type: nextType,
        isDisabled: effectiveDisabled,
        allowTooltip: effectiveDisabled,
        label: labelMap[worklogBtn.id][labelKey],
      };
    }

    return newFloatingButtons;
  }, [
    OFFICE,
    applyWfh,
    applyWfo,
    applyLeave,
    hasRemoteApplicationAccess,
    onRemoteApplicationClick,
    todaySelfAttendance,
    user.timezone,
    workPlanButton,
  ]);

  return isComponentLoading ? (
    <div className="floating-action-container">
      <div className="loading-spinner">
        <FiLoader size={24} />
      </div>
    </div>
  ) : !todaySelfAttendance || isObjectEmpty(todaySelfAttendance) ? (
    <></>
  ) : (
    <FloatingButtons buttons={floatingButtons} />
  );
};

export default WorkPlanButtons;
